import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { IonRadioGroup, PopoverController, ToastController } from '@ionic/angular';
import { Appearance, StripeElementsOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { StripePaymentElementComponent, StripeService } from 'ngx-stripe';
import { PaymentService } from 'src/services/payment.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-stripe-customer-payment-methods',
  templateUrl: './stripe-customer-payment-methods.component.html',
  styleUrls: ['./stripe-customer-payment-methods.component.scss'],
})
export class StripeCustomerPaymentMethodsComponent implements OnInit {
  @Input() isTest: boolean;
  @Input() hideAddNewPayment: boolean;
  @Input() usePopover: boolean;
  @Input() accountId: number;
  @Input() selectedPaymentMethodId: string;
  @Output() paymentMethodSelected = new EventEmitter<any>();
  @ViewChild('newPaymentMethodControl') newPaymentMethodControl: StripePaymentElementComponent;
  @ViewChild('paymentMethodRadioGroup') paymentMethodRadioGroup: IonRadioGroup;

  isLoading = false;
  paymentMethods: any[];
  editPayMethods = false;
  addNewPayment = false;
  appearance: Appearance = {
    theme: 'stripe',
    labels: 'floating',
    variables: {
      colorPrimary: '#673ab7',
    },
  };
  options: StripeElementsOptions;
  paymentOptions: StripePaymentElementOptions;
  stripeKeys: any;

  constructor(
    private paymentService: PaymentService,
    private toastCtrl: ToastController,
    private stripeService: StripeService,
    private popoverCtrl: PopoverController
  ) {}

  async ngOnInit() {
    this.stripeService.changeKey(environment.stripe.forceTestPayment || this.isTest ? environment.stripe.testPublishableKey : environment.stripe.publishableKey);
    await this.getCustomerPayMethods();
  }

  getCardAssetPath(method: any) {
    const supportedCards = ['amex', 'mastercard', 'visa'];
    if (method.card && method.card.brand) {
      if (supportedCards.includes(method.card.brand)) {
        return 'assets/brand-logos/' + method.card.brand + '.png';
      }
    }

    return null;
  }

  async deletePayMethod(paymethod: any) {
    this.isLoading = true;
    await this.paymentService.deletePaymentMethod(this.accountId, paymethod.id, this.isTest);
    this.getCustomerPayMethods();
    this.toastCtrl
      .create({
        message: 'Payment option removed.',
        duration: 2500,
        position: 'bottom',
        color: 'danger',
      })
      .then((t) => t.present());
    this.isLoading = false;
  }

  selectPaymentMethod(e: any) {
    const method = e.detail.value;
    this.selectedPaymentMethodId = method.id;
    if (this.usePopover) {
      this.popoverCtrl.dismiss(method);
    } else {
      this.paymentMethodSelected.emit(method);
    }
  }

  async showNewPayment() {
    this.addNewPayment = !this.addNewPayment; 
    this.editPayMethods = false;
    if (this.addNewPayment) {
      await this.setupNewPaymentControl();
    }
  }

  async addNewPaymentMethod() {   
    this.stripeService.confirmSetup({
      elements: this.newPaymentMethodControl.elements,
      confirmParams: {
        return_url: `${window.location.origin}/mobile-app/completed`
      },
      redirect: 'if_required'
    }).subscribe(async result => {
      console.log(result);
      if (!result.error) {
        const paymentMethodId = typeof result.setupIntent.payment_method == 'string' ? result.setupIntent.payment_method : result.setupIntent.payment_method.id;
        await this.savePaymentMethod(paymentMethodId);
        return true;
      } else {
        console.error(result.error);
        return false;
      }
    });
  }

  private async setupNewPaymentControl() {
    this.isLoading = true; 
    try {
      this.stripeKeys = await this.paymentService.getSetupIntentKeys(this.accountId, this.isTest);
    } catch (err) {
      console.error(err);
      // alert('Error: ' + JSON.stringify(err));
    } finally {
      this.isLoading = false;
    }

    this.paymentOptions = {
      wallets: {
        applePay: 'never',
        googlePay: 'never'
      }
    }

    this.options = {
      clientSecret: this.stripeKeys.paymentIntentClientSecret      
    };
  }

  private async getCustomerPayMethods() {
    this.isLoading = true;
    const customerPaymethods = await this.paymentService.getCustomerPaymentMethods(
      this.accountId,
      this.isTest,
    );
    // console.log({customerPaymethods});
    this.paymentMethods = customerPaymethods as any[];
    this.isLoading = false;
    console.log(customerPaymethods);

    setTimeout(() => {
      if (this.selectedPaymentMethodId && this.paymentMethodRadioGroup) {
        this.paymentMethodRadioGroup.value = this.paymentMethods.find(x => x.id == this.selectedPaymentMethodId);
      }
    }, 50);
  }

  private async savePaymentMethod(paymentMethodId: string) {
    this.isLoading = true;
        try {
          await this.paymentService.savePaymentMethod(paymentMethodId, this.accountId, this.stripeKeys.customerId, this.isTest);
          this.addNewPayment = false;
          await this.getCustomerPayMethods();
        } catch (err) {
          this.toastCtrl.create({
            color: 'danger',
            message: 'Could not add payment method.',
            duration: 2500
          }).then(x => x.present());
        } finally {
          this.isLoading = false;
        }
  }
}
